/**
 * The props EVERY ticket-template preview renders against — the gallery's
 * tiles, the picker's thumbnails, and both wallet schematics — so those
 * surfaces show one card rather than four differently-sourced ones.
 *
 * BRAND IS LIVE. Inside the admin the operator's own identity is already
 * resolved and free: `brandName`/`brandLogo` read the workspace card
 * web's module-scope workspace.sync.ts keeps live for the whole app, and the
 * studio's own fixture sync writes the same cell. They are the two computeds
 * `<ticket-card>` reads for a real buyer's ticket, read here for the same
 * reason and by the same call, so a preview and the ticket it previews can
 * never name or mark the brand differently. An invented studio name shows the
 * operator someone else's ticket while their real one is one read away.
 *
 * THE EVENT AND THE TICKET STAY THE SAMPLE, and not for want of trying:
 *
 *   the ticket NUMBER — there is no order behind a preview, so a real one
 *     would have to be borrowed from a specific buyer's issued ticket. A
 *     design gallery is no place to print one buyer's reference.
 *
 *   the QR — the sample's own nonsense token, encoded for real by the same
 *     pure encoder a shipped card uses, so the slot's geometry is honest
 *     while the credential is not one. A preview hands out no door credential.
 *
 *   the event, venue, schedule and ticket TYPE — the gallery route
 *     (`ticket-templates-view`) carries no event: `consoleEventId` clears when
 *     `<event-console>` disconnects and `<events-sync>` only listens on the
 *     events route, so an event resolved here would be whichever one the
 *     operator last happened to open, or nothing — content that varies with
 *     navigation history rather than with the design being previewed. The
 *     picker's own two hosts do hold a live event draft, but feeding it in
 *     would split the two surfaces' content in half for a control whose whole
 *     job is comparing three compositions of the SAME card.
 *
 * `colors` stays null for `<ticket-card>`'s own reason: no shipped variant
 * reads an accent colour, so passing one hands over a value nothing renders.
 */
import { computed } from "@mhmo91/schmancy/state";
import {
	sampleTicketCardProps,
	type TicketTemplateProps,
} from "@momo/ticket-template";
import {
	brandLogo,
	brandName,
	workspaceCard,
} from "../workspace/workspace.state.ts";

export const ticketPreviewProps = computed<TicketTemplateProps>(() => {
	// The gate is whether there is an operator behind this preview at all, and
	// `brandName` cannot answer it: for storefront chrome it falls back to the
	// generic product name, which on a sample ticket would read as a real
	// claim rather than as the sample it is. `brand.name` is required on a
	// resolved card, so a resolved workspace always has a real one to show.
	if (workspaceCard.value.workspace === null) return sampleTicketCardProps;
	return {
		...sampleTicketCardProps,
		brand: {
			name: brandName.get(),
			logoUrl: brandLogo.get(),
			colors: null,
		},
	};
});
